import { motion } from "framer-motion";
import { CarFront, Warehouse, Building2, ArrowUpRight } from "lucide-react";
import { scrollTo } from "@/App";

const DRIVEWAY_IMG = "/images/dropped-kerb-job.jpg";

const reveal = {
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
};

export default function ServicesBento() {
    return (
        <section id="services" className="relative bg-tarmac py-24 sm:py-32 grain" data-testid="services-section">
            <div className="px-5 sm:px-8 max-w-7xl mx-auto relative z-10">
                <motion.p {...reveal} transition={{ duration: 0.6 }} className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-4">
                    [ What we drop kerbs for ]
                </motion.p>
                <motion.h2 {...reveal} transition={{ duration: 0.7 }} className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl lg:text-7xl mb-16">
                    Anywhere you<br />have to drive up.
                </motion.h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-ash">
                    <motion.div
                        {...reveal}
                        transition={{ duration: 0.7 }}
                        className="md:col-span-2 md:row-span-3 relative border-b border-r border-ash min-h-[320px] md:min-h-[640px] overflow-hidden group"
                        data-testid="service-image-cell"
                    >
                        <img
                            src={DRIVEWAY_IMG}
                            alt="Completed dropped kerb and new driveway installed by Drop Kerbs UK"
                            className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-[opacity,transform] duration-700"
                        />
                        <div className="absolute inset-0 bg-tarmac/40" />
                        <div className="absolute bottom-0 left-0 p-8 sm:p-12">
                            <p className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-3">The result</p>
                            <p className="font-display uppercase text-3xl sm:text-4xl leading-none max-w-sm">
                                Glide in. No more bump & scrape.
                            </p>
                        </div>
                    </motion.div>

                    {[
                        {
                            icon: CarFront,
                            title: "Driveway Access",
                            text: "The classic dropped kerb for parking on your own drive. Stop paying for permits and circling the block.",
                            id: "driveway",
                            img: "/images/driveway-access.jpg",
                            alt: "Fresh tarmac dropped kerb crossing to a block paved driveway by Drop Kerbs UK",
                        },
                        {
                            icon: Warehouse,
                            title: "Garage Access",
                            text: "Level entry into garages and car ports — no more jolting your suspension over a full-height kerb.",
                            id: "garage",
                            img: "/images/garage-access.jpg",
                            alt: "Dropped kerb and smooth tarmac drive into a garage by Drop Kerbs UK",
                        },
                        {
                            icon: Building2,
                            title: "Commercial & Site",
                            text: "Vehicle crossovers for yards, forecourts and developments. Built to highways spec, signed off clean.",
                            id: "commercial",
                            img: "/images/commercial-access.jpg",
                            alt: "New commercial vehicle crossover with fresh tarmac and kerb edging by Drop Kerbs UK",
                        },
                    ].map((s, i) => (
                        <motion.article
                            key={s.id}
                            {...reveal}
                            transition={{ duration: 0.6, delay: 0.1 + i * 0.1 }}
                            className="group border-b border-r border-ash flex flex-col min-h-[220px] hover:bg-coal transition-colors duration-300 overflow-hidden"
                            data-testid={`service-card-${s.id}`}
                        >
                            <div className="h-40 sm:h-44 overflow-hidden border-b border-ash">
                                <img
                                    src={s.img}
                                    alt={s.alt}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                            </div>
                            <div className="p-8 sm:p-10 flex flex-col justify-between flex-1 gap-8">
                                <div className="flex items-start justify-between">
                                    <s.icon size={28} className="text-amber" />
                                    <ArrowUpRight size={20} className="text-ash group-hover:text-amber group-hover:translate-x-1 group-hover:-translate-y-1 transition-[color,transform] duration-300" />
                                </div>
                                <div>
                                    <h3 className="font-display uppercase text-2xl mb-3 tracking-tight">{s.title}</h3>
                                    <p className="text-sm text-grit leading-relaxed">{s.text}</p>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>

                <motion.button
                    {...reveal}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    data-testid="services-quote-btn"
                    onClick={() => scrollTo("#quote")}
                    className="mt-10 px-8 py-4 border border-amber text-amber font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-amber hover:text-tarmac transition-colors duration-200"
                >
                    Tell us where you drive up
                </motion.button>
            </div>
        </section>
    );
}
